import type { HTMLAttributes, ReactElement, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string
  children: ReactNode
}

export function Card({ className, children, ...rest }: CardProps): ReactElement {
  return (
    <div className={cn('card', className)} {...rest}>
      {children}
    </div>
  )
}

export type CardHeaderProps = HTMLAttributes<HTMLDivElement>

export function CardHeader({ className, children, ...rest }: CardHeaderProps): ReactElement {
  return (
    <div className={cn('flex flex-col gap-1.5 px-5 pb-4 pt-5', className)} {...rest}>
      {children}
    </div>
  )
}

export type CardTitleProps = HTMLAttributes<HTMLHeadingElement>

export function CardTitle({ className, children, ...rest }: CardTitleProps): ReactElement {
  return (
    <h2 className={cn('text-base font-semibold leading-tight text-fg', className)} {...rest}>
      {children}
    </h2>
  )
}

export type CardDescriptionProps = HTMLAttributes<HTMLParagraphElement>

export function CardDescription({
  className,
  children,
  ...rest
}: CardDescriptionProps): ReactElement {
  return (
    <p className={cn('text-sm leading-relaxed text-fg-muted', className)} {...rest}>
      {children}
    </p>
  )
}

export type CardBodyProps = HTMLAttributes<HTMLDivElement>

/** `first:pt-5` keeps a header-less card from hugging its top edge. */
export function CardBody({ className, children, ...rest }: CardBodyProps): ReactElement {
  return (
    <div className={cn('px-5 pb-5 pt-0 first:pt-5', className)} {...rest}>
      {children}
    </div>
  )
}

export type CardFooterProps = HTMLAttributes<HTMLDivElement>

export function CardFooter({ className, children, ...rest }: CardFooterProps): ReactElement {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 border-t border-line bg-sunken/40 px-5 py-4',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
}
